import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const SESSION_START_KEY = 'admin-session-start';
const MAX_SESSION_HOURS = 24;

type AppRole = 'admin' | 'moderator' | 'user';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  roleLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [roleLoading, setRoleLoading] = useState(true);

  const checkAdminRole = async () => {
    try {
      // Call the admin_guard RPC function to check admin status
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)('admin_guard');
      
      if (error) {
        console.error('Error calling admin_guard:', error);
        setIsAdmin(false);
      } else {
        // admin_guard returns { ok: boolean }
        const result = data as { ok: boolean } | null;
        setIsAdmin(result?.ok === true);
      }
    } catch (err) {
      console.error('Exception calling admin_guard:', err);
      setIsAdmin(false);
    } finally {
      setRoleLoading(false);
    }
  };

  // Check if session has exceeded 24 hours
  const checkSessionExpiry = () => {
    const sessionStart = localStorage.getItem(SESSION_START_KEY);
    if (sessionStart) {
      const startTime = parseInt(sessionStart, 10);
      const now = Date.now();
      const hoursElapsed = (now - startTime) / (1000 * 60 * 60);
      
      if (hoursElapsed >= MAX_SESSION_HOURS) {
        toast.error('Session expirée', {
          description: 'Votre session de 24 heures a expiré. Veuillez vous reconnecter.',
        });
        supabase.auth.signOut();
        localStorage.removeItem(SESSION_START_KEY);
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        if (event === 'SIGNED_IN' && session?.user) {
          // Store session start time on login
          if (!localStorage.getItem(SESSION_START_KEY)) {
            localStorage.setItem(SESSION_START_KEY, Date.now().toString());
          }
        }
        
        if (event === 'SIGNED_OUT') {
          localStorage.removeItem(SESSION_START_KEY);
        }
        
        if (session?.user) {
          // Check session expiry before checking admin role
          if (!checkSessionExpiry()) {
            setTimeout(() => {
              checkAdminRole();
            }, 0);
          }
        } else {
          setIsAdmin(false);
          setRoleLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        // Check session expiry on initial load
        if (!checkSessionExpiry()) {
          checkAdminRole();
        }
      } else {
        setRoleLoading(false);
      }
    });

    // Periodic check every 5 minutes
    const intervalId = setInterval(() => {
      if (user) {
        checkSessionExpiry();
      }
    }, 5 * 60 * 1000);

    return () => {
      subscription.unsubscribe();
      clearInterval(intervalId);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) {
      localStorage.setItem(SESSION_START_KEY, Date.now().toString());
    }
    return { error };
  };

  const signOut = async () => {
    localStorage.removeItem(SESSION_START_KEY);
    await supabase.auth.signOut();
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, roleLoading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
